<template>
  <div class="upgrade">
    <h4 v-if="requestAccess">
      Current plan for <span>{{ project.name }}</span> project doesn't give you access to this feature.<br>
      <small>To use {{ requestAccess }}, upgrade your account :</small>
    </h4>

    <el-row v-if="client" :gutter="20">
      <el-col v-if="!selected || selected === 'free'" :xl="col" :lg="col" :md="col" :xs="24">
        <div v-bind:class="{ current: !client.team }">
          <ProductDescription compare />
          <div class="price">Free</div>

          <el-button v-if="selected" @click="selected = null" icon="el-icon-back">Back</el-button>
          <el-button v-else-if="!client.team" :disabled="true">Current Plan</el-button>
          <el-button
            v-else
            type="primary"
            :disabled="client.enterprise"
            @click="selected = 'free'"
          >
            Downgrade to Free
          </el-button>
        </div>
      </el-col>
      <el-col v-if="!selected || selected === 'team'" :xl="col" :lg="col" :md="col" :xs="24">
        <div v-bind:class="{ current: client.team && !client.enterprise }">
          <ProductDescription :client="{ team: true }"  compare />
          <div class="price">{{ $root.currency.symbol }}25 <span>per month</span></div>

          <el-button v-if="selected" @click="selected = null" icon="el-icon-back">Back</el-button>
          <el-button v-else-if="!client.team" type="primary" @click="selected = 'team'">Upgrade to Team</el-button>
          <el-button v-else-if="!client.enterprise" :disabled="true">Current Plan</el-button>
          <el-button v-else type="primary" disabled>Downgrade to Team</el-button>
        </div>
      </el-col>
      <el-col v-if="!selected && showEnterprise" :xl="col" :lg="col" :md="col" :xs="24">
        <div v-bind:class="{ current: client.enterprise }">
          <ProductDescription :client="{ team: true, enterprise: true }" compare />
          <div class="price">{{ $root.currency.symbol }}10 <span>per user/month</span></div>
          <el-button type="info">Contact Sales</el-button>
        </div>
      </el-col>
      <el-col v-if="selected" :xl="showEnterprise ? 16 : 12" :lg="showEnterprise ? 16 : 12" :md="showEnterprise ? 16 : 12" :xs="24">
        <BillingPaymentMethod
          :customer.sync="customer"
          :payment-default="paymentMethodDefault"
          @change="load"
        />
        <div>
          <h4><span>{{ textCase('Downgrade', 'Upgrade', downgrade) }} summary</span></h4>
          <el-row class="summary">
            <el-col :span="12">
              <strong>New monthly total</strong>
              <small>Starting {{ starting }}</small>
            </el-col>
            <el-col :span="12" align="right">
              <span v-if="selected === 'free'" class="price">Free</span>
              <span v-else class="price">{{ $root.currency.symbol }}25 <span>per month</span></span>
            </el-col>
          </el-row>
          <el-button
            type="primary"
            large
            :loading="loading"
            :disabled="!paymentMethodDefault"
            @click.prevent="update"
          >
            {{ textCase('Downgrade', 'Upgrade', downgrade) }} my account
          </el-button>
          <p v-if="downgrade" class="notice">
            You are downgrading. After {{ starting }}, runstack features and limits will change.
          </p>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment-timezone'
import BillingPaymentMethod from './PaymentMethod'
import ProductDescription from '../Account/ProductDescription'

export default {
  props: {
    requestAccess: String,
    showEnterprise: {
      type: Boolean,
      default: true
    }
  },
  components: {
    BillingPaymentMethod,
    ProductDescription
  },
  data () {
    return {
      loading: false,
      starting: moment().format('MMM DD, YYYY'),
      selected: null,
      customer: null,
      paymentMethods: [],
      paymentDefault: null
    }
  },
  created () {
    this.load()
  },
  computed: {
    col () {
      return this.showEnterprise ? 8 : 12
    },
    paymentMethodDefault () {
      return this.paymentMethods.find((pm) => pm.id === this.paymentDefault)
    },
    downgrade () {
      return this.selected === 'free'
    },
    ...mapState(['project', 'client'])
  },
  methods: {
    load () {
      this.$store.dispatch('getBilling').then((result) => {
        this.customer = result.customer
        this.paymentMethods = result.paymentMethods
        this.paymentDefault = result.customer.invoice_settings.default_payment_method
      }).catch(() => {})
    },
    update () {
      this.loading = true
      this.$store.dispatch('updateBillingProduct', { product: this.selected }).then(() => {
        this.loading = false
        this.selected = null
        this.$emit('upgrade', true)
        this.$store.dispatch('getTeams')
        this.$nextTick(() => this.$message({
          message: 'Your plan has been updated successfully.',
          type: 'success'
        }))
      }).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../../theme";

  .upgrade {
    max-width: 1000px;

    h4 {
      margin-top: 0;
      font-size: 16px;
      line-height: 25px;

      span {
        color: $--color-primary;
      }
    }

    .el-col > div:not(.el-card) {
      padding: 2em;
      background-color: #fff;
      border-top: 4px solid $--color-primary;
      -webkit-box-shadow: 0 10px 25px 0 rgba(5, 63, 46, 0.1);
      box-shadow: 0 10px 25px 0 rgba(5, 63, 46, 0.1);

      h4 {
        font-size: 2em;
        margin-bottom: 10px;
        font-weight: 400;

        span {
          line-height: 21px;
          font-size: 14px;
          color: $--color-primary;
          margin: 5px 0 25px;
          font-weight: 500;
          padding: 0;
          text-transform: uppercase;
          display: block;
        }
      }

      ul {
        list-style: none;
        padding: 0;
        margin: 0;
        min-height: 160px;

        .mdi {
          color: $--color-success;
        }
        li {
          line-height: 24px;
        }
      }

      .price {
        font-size: 1.75em;
        font-weight: 600;
        margin: 0 0 20px 0;
        line-height: 25.2px;

        span {
          font-weight: 500;
          font-size: 14px;
          letter-spacing: -.25px;
        }
      }

      .el-button {
        width: 100%;
      }

      &.current {
        border-color: $--button-primary-border-color;
        background-color: #42526d;
        color: white;

        h4 {
          span {
            color: $--button-primary-border-color;
          }
        }
        .mdi {
          color: $--button-primary-border-color;
        }
      }
    }

    .el-col > .el-card + div {
      border-color: transparent;
      margin-top: 10px;

      .summary {
        margin-bottom: 20px;
        strong {
          display: block;
        }
        .price {
          font-size: 1.3em;
        }
      }
      .notice {
        font-size: 12px;
        color: $--color-danger;
      }
    }
  }
</style>
